import { c as _c } from "react/compiler-runtime";
import type { JSX } from 'react';
import styled from 'styled-components';
import { colors } from '../../theme/colors';
import { Button } from '../button';
import type { DefinitionListItem } from '../definition-list';
import { DefinitionList } from '../definition-list';
import type { Employee } from '../employee-badge/types';
import { EmployeeThumbnail, squircleClipPath } from '../employee-thumbnail';
import type { EmployeeDetailsPopoverVariant } from '../employees-details-popover/types';
import { IconEmailOutline } from '../icons';
import { MiniIconPerson } from '../mini-icons';
import { Padding } from '../padding';
import { SmallCaps } from '../small-caps';
import { Text } from '../text';
import { TextLink } from '../text-link';
interface Props extends Partial<Pick<Employee, 'title' | 'name' | 'phone' | 'imageUrl' | 'email' | 'extension' | 'departments' | 'team'>> {
  variant?: EmployeeDetailsPopoverVariant;
  onCopyEmail?: () => unknown;
  hideEmailAddress?: boolean;
}
function copyEmail(email: string, onCopyEmail?: () => unknown): void {
  if (email) {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    navigator.clipboard.writeText(email);
    if (onCopyEmail) {
      onCopyEmail();
    }
  }
}
export function EmployeeProfile(t0) {
  const $ = _c(68);
  const {
    imageUrl,
    name,
    title,
    email,
    phone,
    variant,
    onCopyEmail,
    team,
    hideEmailAddress: t1
  } = t0;
  const hideEmailAddress = t1 === undefined ? false : t1;
  let employeeDetailItems;
  if ($[0] !== phone || $[1] !== team || $[2] !== title) {
    employeeDetailItems = [];
    if (title) {
      let t2;
      if ($[4] !== title) {
        t2 = {
          type: undefined,
          title: "Title",
          value: title,
          testId: "employee-title"
        };
        $[4] = title;
        $[5] = t2;
      } else {
        t2 = $[5];
      }
      employeeDetailItems.push(t2);
    }
    if (team) {
      let t2;
      if ($[6] !== team) {
        t2 = {
          type: undefined,
          title: "Team",
          value: team,
          testId: "employee-team"
        };
        $[6] = team;
        $[7] = t2;
      } else {
        t2 = $[7];
      }
      employeeDetailItems.push(t2);
    }
    if (phone) {
      let t2;
      if ($[8] !== phone) {
        t2 = {
          type: undefined,
          title: "Phone",
          value: phone,
          testId: "employee-phone"
        };
        $[8] = phone;
        $[9] = t2;
      } else {
        t2 = $[9];
      }
      employeeDetailItems.push(t2);
    }
    $[0] = phone;
    $[1] = team;
    $[2] = title;
    $[3] = employeeDetailItems;
  } else {
    employeeDetailItems = $[3];
  }
  if (variant === "inline") {
    let t2;
    if ($[10] !== imageUrl) {
      t2 = imageUrl ? <EmployeeThumbnail sizeInPixels={40} imageUrl={imageUrl} variant="round" /> : <StyledFallback variant="round" size={40}><MiniIconPerson color={colors.steel[200]} /></StyledFallback>;
      $[10] = imageUrl;
      $[11] = t2;
    } else {
      t2 = $[11];
    }
    let t3;
    if ($[12] !== name) {
      t3 = <Text color={colors.steel[500]} size="large" testId="name">{name}</Text>;
      $[12] = name;
      $[13] = t3;
    } else {
      t3 = $[13];
    }
    let t4;
    if ($[14] !== employeeDetailItems) {
      t4 = <DefinitionList labelWidth="80px" labelSpacing={12} columnSpacing={0} items={employeeDetailItems} />;
      $[14] = employeeDetailItems;
      $[15] = t4;
    } else {
      t4 = $[15];
    }
    let t5;
    if ($[16] !== t3 || $[17] !== t4) {
      t5 = <Padding left={8}>{t3}{t4}</Padding>;
      $[16] = t3;
      $[17] = t4;
      $[18] = t5;
    } else {
      t5 = $[18];
    }
    let t6;
    if ($[19] !== t2 || $[20] !== t5) {
      t6 = <div className="flex items-center">{t2}{t5}</div>;
      $[19] = t2;
      $[20] = t5;
      $[21] = t6;
    } else {
      t6 = $[21];
    }
    return t6;
  }
  if (variant === "small") {
    let t2;
    if ($[22] !== imageUrl) {
      t2 = imageUrl ? <EmployeeThumbnail sizeInPixels={40} imageUrl={imageUrl} variant="round" /> : <StyledFallback variant="round"><MiniIconPerson color={colors.steel[200]} /></StyledFallback>;
      $[22] = imageUrl;
      $[23] = t2;
    } else {
      t2 = $[23];
    }
    let t3;
    if ($[24] !== title) {
      t3 = title && <SmallCaps testId="employee-title">{title}</SmallCaps>;
      $[24] = title;
      $[25] = t3;
    } else {
      t3 = $[25];
    }
    let t4;
    if ($[26] !== name) {
      t4 = <Text color="dark" testId="employee-name">{name}</Text>;
      $[26] = name;
      $[27] = t4;
    } else {
      t4 = $[27];
    }
    let t5;
    if ($[28] !== t3 || $[29] !== t4) {
      t5 = <Padding left={12}><div className="flex flex-col gap-0">{t3}{t4}</div></Padding>;
      $[28] = t3;
      $[29] = t4;
      $[30] = t5;
    } else {
      t5 = $[30];
    }
    let t6;
    if ($[31] !== t2 || $[32] !== t5) {
      t6 = <div className="flex items-center">{t2}{t5}</div>;
      $[31] = t2;
      $[32] = t5;
      $[33] = t6;
    } else {
      t6 = $[33];
    }
    return t6;
  }
  if (variant === "details" || variant === "expanded") {
    let t2;
    if ($[34] !== imageUrl) {
      t2 = <Padding right={12} top={8}>{imageUrl ? <EmployeeThumbnail sizeInPixels={40} imageUrl={imageUrl} variant="round" /> : <StyledFallback variant="round"><MiniIconPerson color={colors.steel[200]} /></StyledFallback>}</Padding>;
      $[34] = imageUrl;
      $[35] = t2;
    } else {
      t2 = $[35];
    }
    let t3;
    if ($[36] !== name) {
      t3 = <Text color={colors.steel[500]} size="large">{name}</Text>;
      $[36] = name;
      $[37] = t3;
    } else {
      t3 = $[37];
    }
    let t4;
    if ($[38] !== employeeDetailItems) {
      t4 = <DefinitionList labelWidth="80px" labelSpacing={12} columnSpacing={0} items={employeeDetailItems} />;
      $[38] = employeeDetailItems;
      $[39] = t4;
    } else {
      t4 = $[39];
    }
    let t5;
    if ($[40] !== t3 || $[41] !== t4) {
      t5 = <div>{t3}{t4}</div>;
      $[40] = t3;
      $[41] = t4;
      $[42] = t5;
    } else {
      t5 = $[42];
    }
    let t6;
    if ($[43] !== email || $[44] !== onCopyEmail) {
      t6 = email && <div className="flex flex-row gap-4"><Button size="secondary" onClick={() => copyEmail(email, onCopyEmail)}><div className="flex flex-row items-center"><IconEmailOutline size={18} /><Padding left={8}><Text>Copy email address</Text></Padding></div></Button></div>;
      $[43] = email;
      $[44] = onCopyEmail;
      $[45] = t6;
    } else {
      t6 = $[45];
    }
    let t7;
    if ($[46] !== t5 || $[47] !== t6) {
      t7 = <div className="flex flex-col justify-center gap-2">{t5}{t6}</div>;
      $[46] = t5;
      $[47] = t6;
      $[48] = t7;
    } else {
      t7 = $[48];
    }
    let t8;
    if ($[49] !== t2 || $[50] !== t7) {
      t8 = <div className="flex">{t2}{t7}</div>;
      $[49] = t2;
      $[50] = t7;
      $[51] = t8;
    } else {
      t8 = $[51];
    }
    return t8;
  }
  let t2;
  if ($[52] !== imageUrl) {
    t2 = <Padding right={20}>{imageUrl ? <EmployeeThumbnail imageUrl={imageUrl} sizeInPixels={60} /> : <StyledFallback variant="round" size={60}><MiniIconPerson color={colors.steel[200]} /></StyledFallback>}</Padding>;
    $[52] = imageUrl;
    $[53] = t2;
  } else {
    t2 = $[53];
  }
  let t3;
  if ($[54] !== name) {
    t3 = <Text color={colors.steel[500]} testId="employee-name">{name}</Text>;
    $[54] = name;
    $[55] = t3;
  } else {
    t3 = $[55];
  }
  let t4;
  if ($[56] !== employeeDetailItems) {
    t4 = <DefinitionList labelWidth="80px" labelSpacing={12} columnSpacing={0} items={employeeDetailItems} />;
    $[56] = employeeDetailItems;
    $[57] = t4;
  } else {
    t4 = $[57];
  }
  let t5;
  if ($[58] !== email || $[59] !== hideEmailAddress) {
    t5 = email && !hideEmailAddress && <TextLink style={{
      color: colors.steel[500]
    }} href={`mailto:${email}`} testId="employee-email">{email}</TextLink>;
    $[58] = email;
    $[59] = hideEmailAddress;
    $[60] = t5;
  } else {
    t5 = $[60];
  }
  let t6;
  if ($[61] !== t3 || $[62] !== t4 || $[63] !== t5) {
    t6 = <div className="flex flex-col justify-center"><Text size="regular" color="dark">{t3}{t4}{t5}</Text></div>;
    $[61] = t3;
    $[62] = t4;
    $[63] = t5;
    $[64] = t6;
  } else {
    t6 = $[64];
  }
  let t7;
  if ($[65] !== t2 || $[66] !== t6) {
    t7 = <div className="flex">{t2}{t6}</div>;
    $[65] = t2;
    $[66] = t6;
    $[67] = t7;
  } else {
    t7 = $[67];
  }
  return t7;
}
const StyledFallback = styled.div<{
  variant?: string;
  size?: number;
}>`
  height: ${({
  size
}) => size ? `${size}px` : '40px'};
  width: ${({
  size
}) => size ? `${size}px` : '40px'};
  background-color: ${colors.steel[100]};
  display: flex;
  align-items: center;
  justify-content: center;
  ${({
  variant
}) => variant === 'squircle' ? `clip-path: polygon(${squircleClipPath(6)});` : 'border-radius: 50%;'};
`;